import { useEffect, useState, useContext } from 'react';
import { Card } from '@/components/ui/Card';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { ConfigContext } from '@/context';
import { api } from '@/utils/api';
import { handleError } from '@/utils/error';
import { Policy } from '@/types';

export function PrivacyDisclosures() {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const policyClassNames = ['policy-left', 'policy-middle', 'policy-right'];

  const {
    consentCategories,
    privacyDisclosuresName,
    privacyDisclosuresDescription,
  } = useContext(ConfigContext);

  useEffect(() => {
    const getPolicies = async () => {
      const data = (await api.consent.findConsentTypes({
        category: 'disclosure',
      })) as Policy[];
      setPolicies(data);
      setLoading(false);
    };

    api.onConcordReady(() => getPolicies());
  }, []);

  const sortedPolicies = policies
    .filter((urn) => urn.subcategory === 'privacy_policy')
    .sort()
    .concat(
      policies.filter((urn) => urn.subcategory !== 'privacy_policy').sort(),
    );

  /**
   * This is a helper function to support translation of the consent names if
   * the string contains 'agreement', 'privacy policy' or 'terms of service'. It
   * will replace the consent name with the translated value if it matches the
   * key in the replacements object. Names are case insensitive.
   */
  const getConsentText = (policy: Policy): string => {
    const replacements: Record<string, object> = {
      privacy_policy: {
        'privacy policy': consentCategories?.privacy_policy?.title,
        agreement: consentCategories?.agreement?.title,
      },
      terms_of_service: {
        'terms of service': consentCategories?.terms_of_service?.title,
        agreement: consentCategories?.agreement?.title,
      },
    };

    try {
      const subcategoryReplacements = replacements[policy.subcategory];
      if (subcategoryReplacements) {
        const policyNameLower = policy.name.toLowerCase();
        const replacedValues = Object.entries(subcategoryReplacements).map(
          ([key, value]) => {
            const keyLower = key.toLowerCase();
            if (policyNameLower.includes(keyLower)) {
              const regex = new RegExp(key, 'i');
              return policy.name.replace(regex, value);
            }
            return null;
          },
        );
        const replacedValue = replacedValues.find((value) => value !== null);
        if (replacedValue) {
          return replacedValue;
        }
      }
    } catch (error) {
      handleError(error);
    }

    return policy.name;
  };

  return (
    <Card loading={loading} title={privacyDisclosuresName}>
      <p
        className="text-muted-foreground"
        data-testid="cd-privacy-center-description"
      >
        {privacyDisclosuresDescription}
      </p>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {sortedPolicies.map((policy, i) => (
          <div key={i} className={policyClassNames[i % 3]}>
            {policy.url ? (
              <ExternalLink key={i} href={policy.url}>
                {getConsentText(policy)}
              </ExternalLink>
            ) : (
              <>{getConsentText(policy)}</>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}
